import { CAMERA_PLATFORMS } from '../domain/camera-platform';
import { CAMERA_DISTANCES } from '../domain/camera-distance';
import { RENDER_PRESETS } from '../domain/render-preset';
import { frameTimecode } from '../domain/frame-timecode';

export interface RecoveryIssue {
  field: string;
  label: string;
  reason: string;
  savedValue: string;
  replacementValue: string;
  removed: string;
}

const labels: Record<string, string> = {
  title: 'Trace title', placeTitle: 'Opening place title', cameraStyleVersion: 'Camera Platform behavior',
  camera: 'Camera Platform', distance: 'Lens', cameraGimbal: 'Gimbal', cameraAngle: 'Camera angle',
  cinematicEnabled: 'Camera animation', cinematicSequence: 'Camera keyframes', depthOfField: 'Depth of field',
  line: 'Trace line style', mapStyle: 'Map style', trackAppearance: 'Trace appearance', hidePlaces: 'Hide place names',
  terrainExaggeration: 'Terrain height', unitSystem: 'Units', timing: 'Film timing', preset: 'Aspect ratio',
  appearances: 'Video layouts', exportRange: 'Selected footage', keepSourcePace: 'Source pace',
  progress: 'Playhead position', imageRecipe: 'Image settings', sections: 'Expanded controls',
  name: 'Name', region: 'Region', enabled: 'Enabled', chosen: 'Layout selected', presentation: 'Layout',
  targetSeconds: 'Video length in seconds', pace: 'Seconds per kilometer', mode: 'Timing mode',
};

export function recoveryLabel(field: string): string {
  if (field.startsWith('appearances.')) {
    const [, preset] = field.slice('appearances.'.length).split(':');
    return `${RENDER_PRESETS.find(item => item.id === preset)?.label ?? 'Video'} layout`;
  }
  return labels[field] ?? field.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/^./, char => char.toUpperCase());
}

function keyCount(value: unknown): number {
  if (!value || typeof value !== 'object') return 0;
  const sequence = value as { waypoints?: unknown; cameraTracks?: Record<string, unknown> };
  return (Array.isArray(sequence.waypoints) ? sequence.waypoints.length : 0)
    + ['rotation', 'tilt', 'roll', 'altitude', 'lens'].reduce((sum, axis) => sum + (Array.isArray(sequence.cameraTracks?.[axis]) ? sequence.cameraTracks[axis].length : 0), 0);
}

export function describeRecoveryValue(field: string, value: unknown, depth = 0): string {
  if (value === undefined) return 'Unavailable';
  if (value === null) return field === 'exportRange' ? 'Full film' : field === 'cameraAngle' ? 'Automatic' : 'No value';
  if (field === 'cinematicSequence') { const count = keyCount(value); return `${count} camera ${count === 1 ? 'keyframe' : 'keyframes'}`; }
  if (field === 'camera' && typeof value === 'string') return CAMERA_PLATFORMS.find(item => item.id === value)?.label ?? value;
  if (field === 'distance' && typeof value === 'string') return CAMERA_DISTANCES.find(item => item.id === value)?.label ?? value;
  if (field === 'preset' && typeof value === 'string') return RENDER_PRESETS.find(item => item.id === value)?.label ?? value;
  if (field === 'exportRange' && typeof value === 'object') {
    const range = value as { inFrame?: unknown; outFrame?: unknown };
    if (typeof range.inFrame === 'number' && typeof range.outFrame === 'number' && Number.isSafeInteger(range.inFrame) && Number.isSafeInteger(range.outFrame)
      && range.inFrame >= 0 && range.outFrame >= 0) return `IN ${frameTimecode(range.inFrame)} · OUT ${frameTimecode(range.outFrame)}`;
  }
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value !== 'object') return String(value).slice(0, 300);
  if (depth >= 3) return 'Additional saved settings';
  const entries = Object.entries(value);
  if (!entries.length) return 'None';
  const visible = entries.slice(0, 24).map(([key, item]) => `${recoveryLabel(key)}: ${describeRecoveryValue(key, item, depth + 1)}`).join('\n');
  return visible + (entries.length > 24 ? '\nAdditional settings omitted from this summary.' : '');
}

export function recoveryIssue(field: string, saved: unknown, replacement: unknown, reason = 'The saved value is not supported. The default shown below is used instead.'): RecoveryIssue {
  const count = field === 'cinematicSequence' ? keyCount(saved) : 0;
  return { field, label: recoveryLabel(field), reason, savedValue: describeRecoveryValue(field, saved),
    replacementValue: replacement === undefined ? 'Removed' : describeRecoveryValue(field, replacement), removed: count ? `${count} saved camera ${count === 1 ? 'keyframe' : 'keyframes'} will be removed if you accept these changes.` : '' };
}
